// Compact offline-safe status panel reused by /offline-control and /tracking.
// Surfaces the exact signals a traveler in Airplane Mode needs to trust that
// route points are being captured locally.

import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { MapPin, Wifi, WifiOff, CloudOff, Cloud, AlertTriangle, Loader2, Database, AlertCircle } from "lucide-react";

import { useTrackingState, useTrackingStartedAt } from "@/lib/native/tracking-state";
import {
  addConnectionListener,
  getConnectionType,
  type ConnectionType,
} from "@/lib/native/network";
import {
  useLocationPermission,
  requestLocationPermissionSimple,
} from "@/lib/native/location-permission";
import { useGpsStatus } from "@/lib/native/gps-status";
import { useOutboxStats } from "@/lib/native/use-outbox-stats";
import { logDisplayLocalTime } from "@/lib/time/timezone-monitor";
import { useReachability } from "@/lib/connectivity/reachability";
import { useNativeQueueStats, useNextSyncCountdown } from "@/lib/native/tracking-reliability";
import { runSyncNow } from "@/lib/native/run-sync";
import { useInstalledBuildWarning, MIN_RECOMMENDED_APP_VERSION } from "@/lib/native/installed-build-warning";
import { RoavaCard, StatusChip, SecondaryButton, PrimaryButton } from "@/components/roava";
import { openAppSettings } from "@/lib/native/open-app-settings";
import { cn } from "@/lib/utils";

function useConnectionType(): ConnectionType {
  const [t, setT] = useState<ConnectionType>("unknown");
  useEffect(() => {
    let alive = true;
    void getConnectionType().then((v) => { if (alive) setT(v); });
    const sub = addConnectionListener(setT);
    const refresh = () => void getConnectionType().then((v) => { if (alive) setT(v); });
    if (typeof window !== "undefined") {
      window.addEventListener("online", refresh);
      window.addEventListener("offline", refresh);
    }
    return () => {
      alive = false;
      sub.remove();
      if (typeof window !== "undefined") {
        window.removeEventListener("online", refresh);
        window.removeEventListener("offline", refresh);
      }
    };
  }, []);
  return t;
}

function useFmtAgo() {
  const { t } = useTranslation();
  return (ts: number | null, nowMs: number): string => {
    if (!ts) return t("time.none");
    const s = Math.max(0, Math.floor((nowMs - ts) / 1000));
    if (s < 60) return t("time.seconds_ago", { n: s });
    if (s < 3600) return t("time.minutes_ago", { n: Math.floor(s / 60) });
    return t("time.hours_ago", { n: Math.floor(s / 3600) });
  };
}

function useFmtAgoShort() {
  const { t } = useTranslation();
  return (s: number): string => {
    if (s < 60) return t("time.seconds_ago", { n: s });
    if (s < 3600) return t("time.minutes_ago", { n: Math.floor(s / 60) });
    return t("time.hours_ago", { n: Math.floor(s / 3600) });
  };
}

export function OfflineTrackingStatus({ className = "" }: { className?: string }) {
  const { t } = useTranslation();
  const fmtAgo = useFmtAgo();
  const fmtAgoShort = useFmtAgoShort();
  const tracking = useTrackingState();
  const startedAt = useTrackingStartedAt();
  const conn = useConnectionType();
  const perm = useLocationPermission();
  const gps = useGpsStatus();
  const outbox = useOutboxStats();
  const reach = useReachability();
  const nativeQueue = useNativeQueueStats();
  const sync = useNextSyncCountdown();
  const installedBuild = useInstalledBuildWarning();

  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  const linkUp = conn !== "none" && (typeof navigator === "undefined" || navigator.onLine !== false);
  const captive = linkUp && reach.backendReachable === false;
  const online = linkUp && reach.backendReachable !== false;
  const offline = !online;

  const netLabel =
    conn === "none" ? t("tracking.network_offline") :
    captive && conn === "wifi" ? t("tracking.captive_wifi") :
    captive && conn === "cellular" ? t("tracking.captive_cellular") :
    captive ? t("tracking.captive_generic") :
    conn === "wifi" ? t("tracking.network_wifi") :
    conn === "cellular" ? t("tracking.network_cellular") :
    t("tracking.network_unknown");

  const netTone: "good" | "warn" = online ? "good" : "warn";

  const locLabel =
    perm === "granted" ? t("tracking.loc_available") :
    perm === "denied" ? t("tracking.loc_unavailable") :
    perm === "prompt" ? t("tracking.loc_permission_needed") :
    t("tracking.loc_unknown");
  const locTone = perm === "granted" ? "good" : "warn";

  const hasFix = gps.lastFixAt != null;
  const fixAgeSec = hasFix ? Math.max(0, Math.floor((now - gps.lastFixAt!) / 1000)) : null;
  const gpsLine = hasFix
    ? `${fmtAgo(gps.lastFixAt, now)}${gps.lastAccuracy != null ? ` · ±${Math.round(gps.lastAccuracy)} m` : ""}`
    : tracking ? t("tracking.waiting_fix") : t("tracking.no_fix_yet");

  const nativeQueuedForLine = nativeQueue?.native_queue_count ?? 0;
  const fmtQueued = (n: number) => n.toLocaleString();

  const syncSuffix =
    !tracking ? "" :
    sync.syncing ? ` · ${t("common.sending").toLowerCase()}` :
    online && sync.secondsLeft > 0 ? ` · ${t("tracking.next_attempt_in", { n: sync.secondsLeft })}` : "";
  const serverLine =
    outbox.queued > 0
      ? offline
        ? t("tracking.ready_offline", { n: fmtQueued(outbox.queued) })
        : `${t("tracking.ready_to_send", { n: fmtQueued(outbox.queued) })}${syncSuffix || ` · ${t("common.sending").toLowerCase()}`}`
      : `${t("tracking.all_sent")}${syncSuffix}`;
  const serverTone: "good" | "warn" | "muted" =
    outbox.queued > 0 ? (offline ? "warn" : "good") : "good";

  const lastSyncMs = outbox.lastSyncAt ? new Date(outbox.lastSyncAt).getTime() : null;
  const lastSyncAgeSec = lastSyncMs != null ? Math.max(0, Math.floor((now - lastSyncMs) / 1000)) : null;
  const totalQueuedNow = (outbox.queued ?? 0) + nativeQueuedForLine;
  const lastSyncClock = lastSyncMs != null ? new Date(lastSyncMs).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null;
  const queueGrowing = tracking && online && totalQueuedNow > 5 && lastSyncAgeSec != null && lastSyncAgeSec < 120;
  let serverSub: string;
  if (!tracking) {
    serverSub = t("tracking.sub_server_off");
  } else if (offline) {
    serverSub = t("tracking.sub_server_offline");
  } else if (outbox.queued > 0 && sync.syncing) {
    serverSub = t("tracking.sub_server_sending", { n: outbox.queued, count: outbox.queued });
  } else if (queueGrowing) {
    serverSub = t("tracking.sub_server_growing", { ago: fmtAgoShort(lastSyncAgeSec!) });
  } else if (lastSyncAgeSec != null && lastSyncAgeSec > 600 && totalQueuedNow > 0) {
    serverSub = t("tracking.sub_server_stale", { ago: fmtAgoShort(lastSyncAgeSec) });
  } else if (lastSyncClock) {
    serverSub = t("tracking.sub_server_caught_up", { clock: lastSyncClock });
  } else {
    serverSub = t("tracking.sub_server_nothing");
  }

  const stale =
    tracking && perm === "granted" &&
    (!hasFix || (fixAgeSec != null && fixAgeSec > 600));

  const nativeQueued = nativeQueue?.native_queue_count ?? 0;
  const totalQueued = (outbox.queued ?? 0) + nativeQueued;

  let banner: { tone: "good" | "warn" | "bad"; text: string } | null = null;
  if (captive) {
    const netName = conn === "cellular" ? t("tracking.network_cellular") : conn === "wifi" ? t("tracking.network_wifi") : t("tracking.network");
    const detail = totalQueued > 0
      ? t("tracking.banner_captive_queue", { n: totalQueued, count: totalQueued })
      : t("tracking.banner_captive_retry");
    banner = { tone: "warn", text: t("tracking.banner_captive", { net: netName, detail }) };
  } else if (perm === "denied") {
    banner = { tone: "bad", text: t("tracking.banner_loc_denied") };
  } else if (perm === "prompt") {
    banner = { tone: "warn", text: t("tracking.banner_loc_prompt") };
  } else if (tracking && stale && offline) {
    banner = { tone: "warn", text: t("tracking.banner_stale_offline") };
  } else if (tracking && stale) {
    banner = { tone: "warn", text: t("tracking.banner_stale") };
  } else if (tracking && offline && totalQueued > 0) {
    banner = { tone: "good", text: t("tracking.banner_tracking_offline_queue", { n: totalQueued, count: totalQueued }) };
  } else if (tracking && offline) {
    banner = { tone: "good", text: t("tracking.banner_tracking_offline") };
  } else if (tracking && !hasFix) {
    banner = { tone: "warn", text: t("tracking.banner_waiting_fix") };
  } else if (tracking && perm === "granted" && gps.lastError && fixAgeSec != null && fixAgeSec > 120) {
    banner = { tone: "warn", text: t("tracking.banner_loc_unavailable") };
  }


  async function requestPerm() {
    await requestLocationPermissionSimple();
  }

  return (
    <RoavaCard className={className} flush>
      <div className="p-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[var(--roava-text)]">{t("tracking.status_title")}</h3>
          {tracking && (
            <StatusChip variant="success" icon={<span className="size-1.5 rounded-full bg-[var(--roava-green)] animate-pulse" />}>
              {t("tracking.live")}
            </StatusChip>
          )}
        </div>

        {banner && (
          <div
            className={cn(
              "mb-3 rounded-xl px-3 py-2 text-xs font-medium flex items-start gap-2",
              banner.tone === "good"
                ? "bg-[var(--roava-green)]/10 text-[var(--roava-green)]"
                : banner.tone === "warn"
                  ? "bg-[var(--roava-orange)]/15 text-[var(--roava-orange)]"
                  : "bg-[var(--roava-red)]/10 text-[var(--roava-red)]",
            )}
          >
            {banner.tone === "good" ? <MapPin className="size-3.5 mt-0.5 shrink-0" /> : <AlertTriangle className="size-3.5 mt-0.5 shrink-0" />}
            <span>{banner.text}</span>
          </div>
        )}

        {installedBuild.outdated && (
          <div className="mb-3 rounded-xl px-3 py-2 text-xs font-medium flex items-start gap-2 bg-[var(--roava-orange)]/15 text-[var(--roava-orange)]">
            <AlertCircle className="size-3.5 mt-0.5 shrink-0" />
            <span>
              {t("tracking.outdated_build")}
              {installedBuild.appVersion ? ` Installed: v${installedBuild.appVersion}. Recommended: v${MIN_RECOMMENDED_APP_VERSION}+.` : null}
            </span>
          </div>
        )}

        <div className="space-y-1.5 text-sm">
          <Row label={t("tracking.route_tracking")} value={tracking ? (startedAt ? t("tracking.on_since", { time: (() => { const local = new Date(startedAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }); logDisplayLocalTime("tracking-started", local, startedAt); return local; })() }) : t("tracking.on")) : t("tracking.off")} tone={tracking ? "good" : "muted"} icon={tracking ? <Loader2 className="size-3.5 animate-spin" /> : null} />
          <Row label={t("tracking.network")} value={netLabel} tone={netTone} icon={online ? <Wifi className="size-3.5" /> : <WifiOff className="size-3.5" />} />
          <Row label={t("tracking.location")} value={locLabel} tone={locTone} />
          <Row
            label={t("tracking.server_upload")}
            value={serverLine}
            tone={serverTone}
            icon={outbox.queued > 0 ? <CloudOff className="size-3.5" /> : <Cloud className="size-3.5" />}
            sub={serverSub}
          />
          {nativeQueue && (
            <Row
              label={t("tracking.not_yet_uploaded")}
              value={
                nativeQueued === 0
                  ? t("tracking.empty")
                  : `${t("tracking.queued_points", { n: nativeQueued, count: nativeQueued })}${nativeQueue.newest_queued_point_at ? ` · ${t("tracking.newest_ago", { ago: fmtAgo(new Date(nativeQueue.newest_queued_point_at).getTime(), now) })}` : ""}`
              }
              tone={nativeQueued > 0 ? (offline ? "warn" : "muted") : "muted"}
              icon={<Database className="size-3.5" />}
              sub={
                nativeQueued === 0
                  ? t("tracking.sub_local_empty")
                  : offline
                    ? t("tracking.sub_local_offline", { n: nativeQueued, count: nativeQueued })
                    : queueGrowing
                      ? t("tracking.sub_local_growing")
                      : t("tracking.sub_local_default")
              }
            />
          )}
          <Row
            label={t("tracking.last_gps")}
            value={gpsLine}
            tone={hasFix ? "muted" : "warn"}
            sub={
              !hasFix
                ? t("tracking.sub_gps_none")
                : fixAgeSec != null && fixAgeSec > 120
                  ? t("tracking.sub_gps_stale")
                  : gps.lastAccuracy != null && gps.lastAccuracy > 30
                    ? t("tracking.sub_gps_weak", { n: Math.round(gps.lastAccuracy) })
                    : t("tracking.sub_gps_default")
            }
          />

        </div>

        {perm === "denied" ? (
          <>
            <PrimaryButton
              size="sm"
              onClick={() => { void openAppSettings(); }}
              className="mt-3 w-full"
            >
              {t("common.open_settings")}
            </PrimaryButton>
            <p className="text-[11px] text-[var(--roava-text-muted)] mt-2 leading-snug">
              {t("tracking.ios_settings_hint")}
            </p>
          </>
        ) : perm !== "granted" ? (
          <SecondaryButton
            size="sm"
            onClick={requestPerm}
            className="mt-3 w-full"
          >
            {t("tracking.request_permission")}
          </SecondaryButton>
        ) : (
          <SyncNowInlineButton
            inTransit={outbox.queued ?? 0}
            onDevice={nativeQueuedForLine}
          />
        )}


        {tracking && (
          <div className="mt-3 rounded-xl border border-[var(--roava-border)] p-3">
            <p className="text-[11px] font-semibold text-[var(--roava-text)] mb-1.5">{t("tracking.checklist_title")}</p>
            <ul className="text-[11px] text-[var(--roava-text-muted)] leading-snug space-y-1 list-disc pl-4">
              <li>{t("tracking.checklist_always")}</li>
              <li>{t("tracking.checklist_low_power")}</li>
              <li>{t("tracking.checklist_battery")}</li>
              <li>{t("tracking.checklist_reopen")}</li>
              <li>{t("tracking.checklist_outdoors")}</li>
            </ul>
          </div>
        )}
      </div>
    </RoavaCard>
  );
}

function Row({
  label, value, tone, icon, sub,
}: {
  label: string;
  value: string;
  tone: "good" | "warn" | "bad" | "muted";
  icon?: React.ReactNode;
  sub?: string;
}) {
  const toneCls =
    tone === "good" ? "text-[var(--roava-green)]" :
    tone === "warn" ? "text-[var(--roava-orange)]" :
    tone === "bad" ? "text-[var(--roava-red)]" :
    "text-[var(--roava-text)]";
  return (
    <div className="py-0.5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[var(--roava-text-muted)] text-xs">{label}</span>
        <span className={cn("inline-flex items-center gap-1.5 text-xs font-medium", toneCls)}>
          {icon}
          {value}
        </span>
      </div>
      {sub && (
        <p className="text-[10.5px] text-[var(--roava-text-muted)] leading-snug mt-0.5">{sub}</p>
      )}
    </div>
  );
}

function SyncNowInlineButton({ inTransit, onDevice }: { inTransit: number; onDevice: number }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [elapsed, setElapsed] = useState(0);
  const nav = useNavigate();
  const total = inTransit + onDevice;
  const showBreakdown = inTransit > 0 && onDevice > 0;
  const [batchTotal, setBatchTotal] = useState(0);

  useEffect(() => {
    if (!busy || startedAt == null) return;
    const id = window.setInterval(() => {
      setElapsed(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    }, 1000);
    return () => window.clearInterval(id);
  }, [busy, startedAt]);

  useEffect(() => {
    if (!busy) return;
    const id = window.setTimeout(() => setBusy(false), 30_000);
    return () => window.clearTimeout(id);
  }, [busy]);

  const stageLabel = (() => {
    if (elapsed < 3) return t("tracking.stage_reading");
    if (elapsed < 10) return t("tracking.stage_sending");
    if (elapsed < 25) return t("tracking.stage_still_sending");
    return t("tracking.stage_background");
  })();


  return (
    <div className="mt-3">
      <SecondaryButton
        size="sm"
        onClick={async () => {
          if (busy) return;
          setBusy(true);
          setStartedAt(Date.now());
          setElapsed(0);
          setBatchTotal(total);
          try {
            await runSyncNow({ navigate: (p) => nav({ to: p as "/login" }), source: "tracking" });
          } finally {
            setBusy(false);
            setStartedAt(null);
          }
        }}
        className="w-full"
      >
        {busy ? (
          <span className="inline-flex items-center gap-1.5">
            <Loader2 className="size-3.5 animate-spin" />
            {batchTotal > 0
              ? `${stageLabel} ${batchTotal.toLocaleString()} · ${elapsed}s`
              : `${stageLabel} ${elapsed}s`}
          </span>
        ) : total > 0 ? t("tracking.sync_n_now", { n: total.toLocaleString(), count: total }) : t("tracking.sync_now")}
      </SecondaryButton>
      {showBreakdown && !busy && (
        <p className="text-[10.5px] text-[var(--roava-text-muted)] leading-snug mt-1.5 text-center">
          {t("tracking.breakdown", { a: inTransit.toLocaleString(), b: onDevice.toLocaleString() })}
        </p>
      )}
      {busy && elapsed >= 15 && (
        <div className="mt-1.5 space-y-1">
          <p className="text-[10.5px] text-[var(--roava-text-muted)] leading-snug text-center">
            {t("tracking.sync_keeps_running")}
          </p>
          {elapsed >= 30 && (
            <p className="text-[10.5px] text-[var(--roava-text-muted)] leading-snug text-center">
              {t("tracking.why_still_syncing")}
            </p>
          )}
        </div>
      )}

    </div>
  );
}
